import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical, Flag } from 'lucide-react';
import ReportModal from '../../ReportModal';

const ChatMessage = ({ message, isMine }) => {
  const { t } = useTranslation();

  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const formattedTime = new Date(message.createdAt).toLocaleTimeString([], {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Close the per-message menu on outside click / Escape
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event) => {
      const isOutsideButton =
        buttonRef.current && !buttonRef.current.contains(event.target);
      const isOutsideMenu =
        menuRef.current && !menuRef.current.contains(event.target);
      if (isOutsideButton && isOutsideMenu) {
        setShowMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMenu]);

  return (
    <div className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
      <div
        className={`
          relative group
          max-w-[85%] sm:max-w-[80%] px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl
          ${isMine
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-secondary text-foreground rounded-bl-sm'
          }
        `}
      >
        <p className="text-xs sm:text-sm leading-relaxed break-words">{message.content}</p>

        {!isMine && (
          <>
            <button
              ref={buttonRef}
              onClick={() => setShowMenu((v) => !v)}
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white shadow-sm border border-border opacity-30 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              aria-label={t('message_options', 'Message options')}
              aria-expanded={showMenu}
              aria-haspopup="true"
            >
              <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            {showMenu && (
              <div
                ref={menuRef}
                className="absolute top-6 right-0 bg-white rounded-xl shadow-lg border border-border w-44 overflow-hidden z-20"
                role="menu"
                aria-orientation="vertical"
              >
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowReportModal(true);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                  role="menuitem"
                >
                  <Flag className="w-4 h-4" />
                  {t('report_message', 'Report Message')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <span className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 sm:mt-1 px-1">
        {formattedTime}
      </span>

      {!isMine && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          targetType="message"
          targetId={message._id}
          targetLabel={t('this_message', 'this message')}
        />
      )}
    </div>
  );
};

export default ChatMessage;
